import { useCallback, useEffect, useRef, useState } from 'react';
import { OUTLINE_CHANGED } from './useBookPath';
import {
  clearPlotTrackChapterBreak,
  commitPlotTrackChapterBreak,
  createPlotTrackRow,
  checkPlotTrackOutline,
  getPlotTrackGeneratePrecheck,
  generatePlotTrack,
  mapPlotTrack,
  placePlotTrackScene,
  setPlotTrackHeaderSpan,
  suggestPlotTrackChapterBreaks,
  updatePlotTrackRow,
  setPlotTrackWhoseHead,
  upsertPlotTrackCell,
  applyPlotTrackWhenTimelines,
  createPlotTrackWhenLabel,
  renamePlotTrackWhenLabel,
  reorderPlotTrackWhenLabels,
  deletePlotTrackWhenLabel,
  setPlotTrackSceneWhen,
  setPlotTrackScenePerson,
  setPlotTrackColumnWidth,
  resetPlotTrackColumnWidths,
  setPlotTrackOneColumnWidth,
  addPlotTrackScene,
  editPlotTrackScene,
  suggestPlotTrackSceneTitle,
} from '../api/plottrack';
import { deleteSection as deleteOutlineSection, renameSection } from '../api/storyBoard';
import { isAbortError, LONG_REQUEST_TIMEOUT_MS } from '../api/client';
import { rejectReadyFields } from '../utils/plottrackGrid';

// Between grid reads after Accept. Inside 1–2s: soon enough to show scenes,
// long enough that an unmapped book is not mapped in a tight loop.
const ACCEPT_GRID_REREAD_PAUSE_MS = 1500;

function pauseUnlessAborted(ms, signals) {
  const list = (signals || []).filter(Boolean);
  return new Promise((resolve) => {
    if (list.some((signal) => signal.aborted)) {
      resolve(false);
      return;
    }
    let settled = false;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      list.forEach((signal) => signal.removeEventListener('abort', onAbort));
      resolve(ok);
    };
    const onAbort = () => finish(false);
    const timer = setTimeout(() => finish(true), ms);
    list.forEach((signal) => signal.addEventListener('abort', onAbort));
  });
}

function stoppedRequest(err) {
  return isAbortError(err) || err?.name === 'AbortError';
}

function gridCreateRaced(err) {
  const status = Number(err?.status);
  const code = String(err?.code || '');
  const message = String(err?.message || '');
  return status === 409 || code === 'ALREADY_EXISTS' || /already exists/i.test(message);
}

function sceneCount(view) {
  return (view?.columns || []).filter((column) => column?.scene_id).length;
}

function linkedSceneCount(view) {
  const ids = new Set();
  (view?.headers?.sequences || []).forEach((sequence) => {
    (sequence?.scene_ids || []).forEach((id) => ids.add(String(id)));
  });
  return (view?.columns || []).filter(
    (column) => column?.scene_id && ids.has(String(column.scene_id)),
  ).length;
}

/** The grid that shows more written scenes. A later empty read loses. */
function fullerGrid(current, incoming) {
  if (!incoming) return current || null;
  if (!current) return incoming;
  const incomingScenes = sceneCount(incoming);
  const currentScenes = sceneCount(current);
  if (incomingScenes !== currentScenes) {
    return incomingScenes > currentScenes ? incoming : current;
  }
  return linkedSceneCount(incoming) > linkedSceneCount(current) ? incoming : current;
}

/**
 * Load the PlotTrack grid via POST /map. Cell writes and tray place
 * go through the existing store — not a second store.
 */
export default function usePlotTrack(storyId) {
  const [view, setView] = useState(null);
  const [fetching, setFetching] = useState(Boolean(storyId));
  const [loadedStoryId, setLoadedStoryId] = useState(null);
  const [trackedStoryId, setTrackedStoryId] = useState(storyId);
  const [error, setError] = useState(null);
  const [chapterSuggestions, setChapterSuggestions] = useState([]);
  const requestRef = useRef(0);
  const abortRef = useRef(null);
  const racedRetryRef = useRef(false);
  const viewRef = useRef(null);
  const loadedStoryRef = useRef(loadedStoryId);
  const storyIdRef = useRef(storyId);
  viewRef.current = view;
  loadedStoryRef.current = loadedStoryId;
  storyIdRef.current = storyId;

  // The story can arrive a render after mount. Stay closed until this
  // story's map lands. Do not paint the previous book's grid in that gap.
  // A null id for one frame is not that gap: clearing here is the
  // "Opening Plot Grid…" takeover while a reply is on screen.
  if (trackedStoryId !== storyId && (storyId || !view)) {
    setTrackedStoryId(storyId);
    setView(null);
    setError(null);
    setLoadedStoryId(null);
    setFetching(Boolean(storyId));
    racedRetryRef.current = false;
  }

  const reload = useCallback(async ({ silent = false, hold = false } = {}) => {
    const requested = storyId;
    const token = requestRef.current + 1;
    requestRef.current = token;
    if (!requested) {
      if (!viewRef.current) {
        setView(null);
        setLoadedStoryId(null);
        setFetching(false);
        setError(null);
      }
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const showing = Boolean(viewRef.current) && loadedStoryRef.current === requested;
    if (!silent && !hold && !showing) setFetching(true);
    if (!hold) setError(null);
    const mapOptions = {
      signal: controller.signal,
      // A fresh outline of many sequences can outlast an ordinary read.
      ...(hold ? { timeoutMs: LONG_REQUEST_TIMEOUT_MS } : {}),
    };
    const applyMap = (next) => {
      // hold: the caller paints the grid, so a token change cannot drop it.
      if (hold || requestRef.current !== token) return next;
      rejectReadyFields(next);
      setView(next);
      setLoadedStoryId(requested);
      setError(null);
      return next;
    };
    const fail = (err) => {
      // A quiet reread must not replace the grid with the load-error page.
      if (hold) return null;
      setError(err);
      if (!silent && !viewRef.current) setView(null);
      setLoadedStoryId(requested);
      return null;
    };
    try {
      const next = await mapPlotTrack(requested, mapOptions);
      return applyMap(next);
    } catch (err) {
      if (stoppedRequest(err) || requestRef.current !== token || controller.signal.aborted) {
        return null;
      }
      if (gridCreateRaced(err) && !racedRetryRef.current) {
        racedRetryRef.current = true;
        try {
          const next = await mapPlotTrack(requested, mapOptions);
          return applyMap(next);
        } catch (retryErr) {
          if (stoppedRequest(retryErr) || requestRef.current !== token || controller.signal.aborted) {
            return null;
          }
          return fail(retryErr);
        }
      }
      return fail(err);
    } finally {
      if (!silent && !hold && requestRef.current === token) setFetching(false);
    }
  }, [storyId]);

  const showingThisStory = Boolean(view) && loadedStoryId === storyId;
  const isLoading = Boolean(storyId) && !showingThisStory && (fetching || loadedStoryId !== storyId);

  const reloadRef = useRef(reload);
  reloadRef.current = reload;
  useEffect(() => {
    if (!storyId) return undefined;
    setChapterSuggestions([]);
    reloadRef.current();
    return () => { abortRef.current?.abort(); };
  }, [storyId]);

  useEffect(() => {
    if (!storyId || typeof window === 'undefined') return undefined;
    const onOutline = () => { reloadRef.current(); };
    window.addEventListener(OUTLINE_CHANGED, onOutline);
    return () => window.removeEventListener(OUTLINE_CHANGED, onOutline);
  }, [storyId]);

  const setWhoseHead = useCallback(async (sceneId, { choice, entityId, bibleEntryId, entityName } = {}) => {
    const saved = await setPlotTrackWhoseHead(storyId, sceneId, { choice, entityId, bibleEntryId });
    setView((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        columns: (prev.columns || []).map((column) => (
          String(column.scene_id) === String(sceneId)
            ? {
              ...column,
              pov: {
                entity_id: saved?.entity_id || null,
                entity_name: saved?.entity_name || entityName || null,
                source: saved?.source || (choice === 'not_set' ? null : 'scene'),
                choice: saved?.choice || choice,
              },
            }
            : column
        )),
      };
    });
    return saved;
  }, [storyId]);

  // When labels (spec §1). Label edits reload the board so every column shows the new words.
  const setSceneWhen = useCallback(async (sceneId, labelId) => {
    const saved = await setPlotTrackSceneWhen(storyId, sceneId, labelId);
    setView((prev) => {
      if (!prev) return prev;
      const label = (prev.when_labels || []).find((item) => String(item.id) === String(labelId));
      return {
        ...prev,
        columns: (prev.columns || []).map((column) => (
          String(column.scene_id) === String(sceneId)
            ? { ...column, when: label ? { id: label.id, label: label.label } : null, when_offer: label ? null : column.when_offer }
            : column
        )),
      };
    });
    return saved;
  }, [storyId]);

  // People named in a scene (spec §2): the server works out offers, so reload the board.
  const setScenePerson = useCallback(async (sceneId, entryId, inScene) => {
    const saved = await setPlotTrackScenePerson(storyId, sceneId, entryId, inScene);
    await reload({ silent: true });
    return saved;
  }, [storyId, reload]);

  // Column width (spec §3). Optimistic: the grid redraws at once, then saves.
  const setColumnWidth = useCallback(async (width) => {
    setView((prev) => (prev ? { ...prev, column_width: width } : prev));
    return setPlotTrackColumnWidth(storyId, width);
  }, [storyId]);

  const setOneColumnWidth = useCallback(async (columnId, widthPx) => {
    setView((prev) => (prev ? {
      ...prev,
      columns: (prev.columns || []).map((column) => (
        String(column.id) === String(columnId) ? { ...column, width_px: widthPx } : column
      )),
    } : prev));
    return setPlotTrackOneColumnWidth(storyId, columnId, widthPx);
  }, [storyId]);

  const resetColumnWidths = useCallback(async () => {
    setView((prev) => (prev ? {
      ...prev,
      columns: (prev.columns || []).map((column) => ({ ...column, width_px: null })),
    } : prev));
    return resetPlotTrackColumnWidths(storyId);
  }, [storyId]);

  // Run 53: one click for the timelines Generate named; reload so every column shows its label.
  const applyWhenTimelines = useCallback(async () => {
    const out = await applyPlotTrackWhenTimelines(storyId);
    await reload({ silent: true });
    return out;
  }, [storyId, reload]);

  const createWhenLabel = useCallback(async (label) => {
    const created = await createPlotTrackWhenLabel(storyId, label);
    setView((prev) => {
      if (!prev) return prev;
      const labels = prev.when_labels || [];
      if (labels.some((item) => String(item.id) === String(created.id))) return prev;
      return { ...prev, when_labels: [...labels, created] };
    });
    return created;
  }, [storyId]);

  const renameWhenLabel = useCallback(async (labelId, label) => {
    const saved = await renamePlotTrackWhenLabel(storyId, labelId, label);
    await reload();
    return saved;
  }, [storyId, reload]);

  const reorderWhenLabels = useCallback(async (labelIds) => {
    const saved = await reorderPlotTrackWhenLabels(storyId, labelIds);
    setView((prev) => (prev ? { ...prev, when_labels: saved?.labels || prev.when_labels } : prev));
    return saved;
  }, [storyId]);

  const deleteWhenLabel = useCallback(async (labelId) => {
    const saved = await deletePlotTrackWhenLabel(storyId, labelId);
    await reload();
    return saved;
  }, [storyId, reload]);

  const saveCell = useCallback(async ({ rowId, columnId, state, note, beats }) => {
    rejectReadyFields({ state, note, beats });
    const saved = await upsertPlotTrackCell(storyId, { rowId, columnId, state, note, beats });
    setView((prev) => {
      if (!prev) return prev;
      const others = (prev.cells || []).filter(
        (cell) => !(String(cell.row_id) === String(rowId) && String(cell.column_id) === String(columnId)),
      );
      return {
        ...prev,
        cells: saved.stored === false ? others : [...others, saved],
      };
    });
    return saved;
  }, [storyId]);

  const placeScene = useCallback(async ({ sceneId, parentSectionId }) => {
    const next = await placePlotTrackScene(storyId, { sceneId, parentSectionId });
    setView(next);
    return next;
  }, [storyId]);

  const setHeaderSpan = useCallback(async ({ headerId, startSceneId, endSceneId }) => {
    const next = await setPlotTrackHeaderSpan(storyId, { headerId, startSceneId, endSceneId });
    setView(next);
    return next;
  }, [storyId]);

  const checkOutline = useCallback(async () => {
    return checkPlotTrackOutline(storyId);
  }, [storyId]);

  const generatePrecheck = useCallback(async () => {
    return getPlotTrackGeneratePrecheck(storyId);
  }, [storyId]);

  const generate = useCallback(async (opts = {}) => {
    const next = await generatePlotTrack(storyId, opts);
    rejectReadyFields(next);
    const preview = Boolean(next?.offer) && next?.wrote !== true;
    if (!preview) setView(next);
    return next;
  }, [storyId]);

  const assess = useCallback(async ({ use_notes = false, part_name = '' } = {}) => {
    const next = await generatePlotTrack(storyId, {
      assess_only: true,
      use_notes,
      part_name,
    });
    rejectReadyFields(next);
    return next;
  }, [storyId]);

  const acceptGenerate = useCallback(async (opts = {}) => {
    const next = await generatePlotTrack(storyId, {
      accept: true,
      replace: opts.replace === true || opts.apply_mode === 'replace',
      ...opts,
    });
    rejectReadyFields(next);
    // A preview, or any reply that did not write, paints at once. A book
    // that never Accepted does not enter the reread loop.
    if (next?.wrote !== true) {
      setView(next);
      return next;
    }
    // Accept writes the scenes, then the page has to read them back. The
    // first read can still be the old empty grid. Keep reading, quietly,
    // until a read has scene columns. The accept reply wins when it already
    // shows more. Stop at the same 180s limit these reads already use.
    // Past that, or if this book is left, return null: the caller keeps the
    // waiting offer instead of painting an empty grid.
    const storyAtStart = storyId;
    const deadline = Date.now() + LONG_REQUEST_TIMEOUT_MS;
    let mapped = null;
    const cancelled = () => (
      storyIdRef.current !== storyAtStart || opts.signal?.aborted
    );

    while (true) {
      if (cancelled() || Date.now() >= deadline) return null;
      mapped = await reload({ silent: true, hold: true });
      if (cancelled()) return null;
      if (mapped == null) {
        if (sceneCount(next) > 0) break;
        return null;
      }
      if (sceneCount(mapped) > 0 || sceneCount(next) > 0) break;
      const remaining = deadline - Date.now();
      if (remaining < ACCEPT_GRID_REREAD_PAUSE_MS) return null;
      const stayed = await pauseUnlessAborted(
        ACCEPT_GRID_REREAD_PAUSE_MS,
        [abortRef.current?.signal, opts.signal],
      );
      if (!stayed || cancelled()) return null;
    }

    if (mapped) rejectReadyFields(mapped);
    if (cancelled()) return null;
    const chosen = fullerGrid(next, mapped);
    setView((prev) => {
      if (storyIdRef.current !== storyAtStart) return prev;
      return fullerGrid(prev, chosen) || chosen;
    });
    return chosen;
  }, [storyId, reload]);

  const addRow = useCallback(async ({ type, label, visibility } = {}) => {
    rejectReadyFields({ type, label, visibility });
    const saved = await createPlotTrackRow(storyId, { type, label, visibility });
    await reload({ silent: true });
    return saved;
  }, [storyId, reload]);

  // Run 36: a scene after any scene, then a silent reload so the grid shows it in place.
  const addScene = useCallback(async ({ afterSceneId, whatHappens, title } = {}) => {
    const saved = await addPlotTrackScene(storyId, { afterSceneId, whatHappens, title });
    await reload({ silent: true });
    return saved;
  }, [storyId, reload]);

  // Run 48: rename or move a scene, then a silent reload so the grid shows it in place.
  const editScene = useCallback(async ({
    sceneId, title, sequenceId, afterSceneId, atStart, beats, rowId,
  } = {}) => {
    const saved = await editPlotTrackScene(storyId, sceneId, {
      title, sequenceId, afterSceneId, atStart, beats, rowId,
    });
    await reload({ silent: true });
    return saved;
  }, [storyId, reload]);

  const deleteScene = useCallback(async (sceneId) => {
    await deleteOutlineSection(sceneId);
    await reload({ silent: true });
  }, [reload]);

  const suggestSceneTitle = useCallback(async (whatHappens) => {
    const out = await suggestPlotTrackSceneTitle(storyId, whatHappens);
    return out?.title || '';
  }, [storyId]);

  const saveRow = useCallback(async (rowId, body) => {
    rejectReadyFields(body);
    const saved = await updatePlotTrackRow(storyId, rowId, body);
    await reload({ silent: true });
    return saved;
  }, [storyId, reload]);

  const dropChapterSuggestion = useCallback((afterSceneId) => {
    setChapterSuggestions((prev) => (
      (prev || []).filter((item) => String(item.after_scene_id) !== String(afterSceneId))
    ));
  }, []);

  const suggestChapterBreaks = useCallback(async () => {
    const suggestions = await suggestPlotTrackChapterBreaks(storyId);
    setChapterSuggestions(suggestions || []);
    return suggestions || [];
  }, [storyId]);

  const commitChapterBreak = useCallback(async (afterSceneId, displayName) => {
    const saved = await commitPlotTrackChapterBreak(storyId, afterSceneId);
    const wanted = String(displayName || '').trim();
    const given = String(saved?.display_name || '').trim();
    if (wanted && saved?.id && wanted !== given) {
      await renameSection(saved.id, wanted);
    }
    dropChapterSuggestion(afterSceneId);
    await reload({ silent: true });
    return saved;
  }, [storyId, dropChapterSuggestion, reload]);

  const clearChapterBreak = useCallback(async (chapterId) => {
    const saved = await clearPlotTrackChapterBreak(storyId, chapterId);
    await reload({ silent: true });
    return saved;
  }, [storyId, reload]);

  const acceptChapterSuggestion = useCallback(async (afterSceneId) => {
    return commitChapterBreak(afterSceneId);
  }, [commitChapterBreak]);

  return {
    storyId,
    view,
    isLoading,
    error,
    reload,
    saveCell,
    setWhoseHead,
    setSceneWhen,
    setScenePerson,
    setColumnWidth,
    setOneColumnWidth,
    resetColumnWidths,
    createWhenLabel,
    applyWhenTimelines,
    renameWhenLabel,
    reorderWhenLabels,
    deleteWhenLabel,
    placeScene,
    setHeaderSpan,
    generate,
    checkOutline,
    generatePrecheck,
    assess,
    acceptGenerate,
    addRow,
    addScene,
    editScene,
    deleteScene,
    suggestSceneTitle,
    saveRow,
    chapterSuggestions,
    suggestChapterBreaks,
    commitChapterBreak,
    clearChapterBreak,
    acceptChapterSuggestion,
    dismissChapterSuggestion: dropChapterSuggestion,
    rows: view?.rows || [],
    columns: view?.columns || [],
    whenLabels: view?.when_labels || [],
    peoplePool: view?.people_pool || [],
    columnWidth: view?.column_width || 'standard',
    cells: view?.cells || [],
    tray: view?.tray || [],
    headers: view?.headers || { acts: [], sequences: [], chapters: [] },
    generated: view?.generated === true,
    disclaimer: view?.disclaimer || null,
    empty_reason: (
      (view?.columns || []).length === 0
        ? (view?.empty_reason || null)
        : null
    ),
    wrote: view?.wrote === true,
    onWriterPath: view?.on_writer_path === true,
    framework_ref: view?.framework_ref || null,
    generate_readiness: view?.generate_readiness || null,
  };
}
